import Link from 'next/link';
import styled, { css } from 'styled-components';
import Button from '../buttons/Button';
import { Text } from '../typography';

interface UseWideDisplay {
  $useWideDisplay?: boolean;
}

interface Loading {
  $isLoading?: boolean;
}

const mobileBreakpoint = `max-width: 700px`;

export const Container = styled.li<UseWideDisplay & Loading>`
  position: relative;
  display: flex;
  flex-direction: column;

  .swiper-button-next,
  .swiper-button-prev {
    color: ${({ theme }) => theme.white};
  }

  .swiper-pagination-bullet {
    background: ${({ theme }) => theme.white};
    opacity: 1;
  }

  .swiper-pagination-bullet-active {
    background: ${({ theme }) => theme.primary};
  }

  .swiper-button-next::after,
  .swiper-button-prev::after {
    font-size: 32px;
  }
`;

export const LinkContainer = styled(Link)<
  UseWideDisplay & { $border: boolean; $isGold: boolean; $disableHover?: boolean }
>`
  display: flex;
  height: 100%;
  flex-direction: column;
  background-color: ${({ theme }) => theme.background};
  flex: 1;
  overflow: hidden;
  box-shadow: 0 2px 4px 2px rgba(0, 0, 0, 0.1);

  &:visited h2 {
    color: ${({ theme }) => theme.backgroundMid};
  }

  &:hover {
    background-color: ${({ theme, $disableHover }) => ($disableHover ? theme.background : theme.backgroundLighter)};
  }

  ${({ $useWideDisplay, $border, $isGold }) => {
    if ($useWideDisplay && $border) {
      return css`
        border-radius: 5px 5px 10px 10px;
        border: 3px solid #fff;
        border-bottom: ${$isGold ? '6' : '3'}px solid #fff;

        @media (${mobileBreakpoint}) {
          border-radius: 10px;
          border-bottom: 3px solid #fff;
        }
      `;
    }

    if ($border) {
      return css`
        border-radius: 10px;
        border: 3px solid #fff;
        border-bottom: 6px solid #fff;
      `;
    }

    return css`
      border-radius: 0 0 10px 10px;
    `;
  }}

  @media (${mobileBreakpoint}) {
    // Fixes box height on iOS 10
    height: auto;
    flex-direction: column-reverse;
  }
`;

export const Content = styled.div<UseWideDisplay>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      flex-direction: row;

      @media (${mobileBreakpoint}) {
        flex-direction: column;
      }
    `}
`;

export const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ImageContent = styled.div<
  UseWideDisplay & { $isPrime: boolean; $showPinnedDevelopmentPropertyCarousel: boolean }
>`
  position: relative;
  overflow: hidden;

  ${({ $useWideDisplay, $isPrime, $showPinnedDevelopmentPropertyCarousel }) =>
    $useWideDisplay &&
    css`
      flex-basis: calc(${$showPinnedDevelopmentPropertyCarousel ? 58 : $isPrime ? 50 : 44}% - 5px);

      @media (${mobileBreakpoint}) {
        flex-basis: initial;
        width: 100%;
        height: auto;
        min-width: 0px;
        max-width: none;
      }
    `}
`;

export const ViewsPill = styled.p`
  padding: 4px 10px;
  background-color: ${({ theme }) => theme.white};
  border-radius: 20px;
  color: ${({ theme }) => theme.textDark};
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 2;
  font-size: 14px;
`;

export const TextBox = styled.div<UseWideDisplay>`
  position: relative;
  padding: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 15px;

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      min-height: 100%;

      @media (${mobileBreakpoint}) {
        min-height: initial;
      }
    `}
`;

export const PinnedTagLine = styled.div`
  padding: 15px 15px 0 15px;
  flex: 1;
  display: flex;

  @media (${mobileBreakpoint}) {
    padding-top: 0;
  }
`;

export const TextBoxTop = styled.div<UseWideDisplay & { $extraPadding: boolean; $pinnedProperty: boolean }>`
  padding-right: ${({ $extraPadding, $useWideDisplay, $pinnedProperty }) =>
    !$extraPadding && !$useWideDisplay ? '0' : $pinnedProperty ? '70' : '115'}px;

  @media (${mobileBreakpoint}) {
    padding-right: ${({ $extraPadding }) => ($extraPadding ? '115' : '0')}px;
  }
`;

export const TextBoxBottom = styled.div<UseWideDisplay>`
  padding-right: 125px;

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      padding-right: 0;

      @media (${mobileBreakpoint}) {
        padding-right: 125px;
      }
    `}
`;

export const AccountLogos = styled.div<UseWideDisplay & { $enhancedBranding: boolean }>`
  position: absolute;

  img {
    max-width: 60px;
  }

  ${({ $useWideDisplay, $enhancedBranding }) => {
    if ($useWideDisplay) {
      return css`
        right: ${$enhancedBranding ? '0' : '15px'};
        top: ${$enhancedBranding ? '0' : '15px'};

        @media (${mobileBreakpoint}) {
          bottom: ${$enhancedBranding ? '0' : '15px'};
          top: initial;
        }
      `;
    }

    return css`
      right: ${$enhancedBranding ? '0' : '15px'};
      bottom: ${$enhancedBranding ? '0' : '15px'};
    `;
  }}
`;

export const DeveloperLogos = styled.div<UseWideDisplay>`
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  gap: 10px;
  align-items: ${({ $useWideDisplay }) => ($useWideDisplay ? 'center' : 'flex-end')};

  p {
    font-size: 12px;
    color: ${({ theme }) => theme.backgroundDark};
    margin: 0 0 5px;
    text-align: center;
  }

  img {
    max-height: 40px;
  }

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      flex-direction: column;

      @media (${mobileBreakpoint}) {
        flex-direction: row-reverse;
        align-items: flex-end;
      }
    `}
`;

export const SellingAgentLogos = styled.div<UseWideDisplay>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;

  img {
    max-height: 28px;
  }

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      flex-direction: column;

      @media (${mobileBreakpoint}) {
        flex-direction: row;
      }
    `}
`;

export const AccountLogo = styled.div<{ $useWideDisplay?: boolean }>`
  width: 116px;
  height: 116px;
  background-size:
    90px auto,
    contain;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 5px 0 0 0;

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      border-radius: 0 0 0 5px;

      @media (${mobileBreakpoint}) {
        border-radius: 5px 0 0 0;
      }
    `}
`;

export const ContactText = styled.p`
  display: inline-block;
  background-color: ${({ theme }) => theme.backgroundLighter};
  border-radius: 6px;
  padding: 3px 6px;
  font-size: 12px;
  font-weight: bold;
  color: ${({ theme }) => theme.textDark};
  margin-bottom: 10px;
`;

export const BottomRow = styled.div<{ $color?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  button {
    display: flex;
    font-size: 14px;
    color: ${({ theme }) => theme.textDark};
    margin-right: 10px;
    align-items: center;

    svg {
      margin-right: 6px;
    }

    &:hover {
      color: ${({ theme, $color }) => $color || theme.primary};
    }
  }
`;

export const DevelopmentBoxFiller = styled.div<UseWideDisplay>`
  padding-top: 60px;

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      padding-top: 0;

      @media (${mobileBreakpoint}) {
        padding-top: 60px;
      }
    `}
`;

export const OptionsRow = styled.div<UseWideDisplay>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 8px;
  right: 15px;

  button span {
    font-size: 14px;
  }

  ${({ $useWideDisplay }) =>
    $useWideDisplay &&
    css`
      bottom: 8px;
      top: initial;

      @media (${mobileBreakpoint}) {
        top: 8px;
        bottom: initial;
      }
    `}
`;

export const HeroNoImage = styled.img`
  display: block;
  width: 100%;
  background-color: ${(props) => props.theme.backgroundLighter};
`;

export const HiddenAddress = styled.span`
  color: ${({ theme }) => theme.backgroundLight};
  font-weight: bold;
`;

export const HiddenContainer = styled.div<UseWideDisplay>`
  width: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;

  ${({ $useWideDisplay }) =>
    $useWideDisplay
      ? css`
          flex-direction: row;
          justify-content: space-between;

          ${HiddenAddress} {
            flex: 1 1 auto;
          }
        `
      : css`
          height: 100%;
          flex-direction: column;
          justify-content: center;
          text-align: center;
        `}
`;

export const UnhideContainer = styled.div`
  width: fit-content;
`;

export const UnhidePropertyButton = styled(Button)`
  background-color: ${({ theme }) => theme.background};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.keyline};
  padding: 8px;
  height: auto;
  transition:
    border-color 300ms linear,
    background-color 300ms linear;

  svg {
    color: ${({ theme }) => theme.primary};
  }

  ${Text} {
    color: ${({ theme }) => theme.textDark};
    font-weight: 600;
    transition: color 300ms linear;
  }

  &:hover {
    background-color: ${({ theme }) => theme.background};
    border-color: ${({ theme }) => theme.primary};

    ${Text} {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

export const AgentHeader = styled.div<{ color?: string }>`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 100px;
  background-color: ${(props) => props.theme.primary};
  gap: 15px;

  img {
    max-width: 31%;
    max-height: 64px;
    width: auto;
    height: auto;
  }
`;

export const ThumbImage = styled.div`
  flex: 1;

  @media (${mobileBreakpoint}) {
    &:nth-child(n + 4) {
      display: none;
    }
  }
`;

export const CentralThumbImages = styled.div<UseWideDisplay & { tier: string; pinnedProperty?: boolean }>`
  display: ${({ tier }) => (tier === 'GOLD' ? 'none' : 'flex')};
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 0 10px 10px;
  order: ${({ $useWideDisplay }) => ($useWideDisplay ? 1 : -1)};

  ${ThumbImage} {
    height: ${({ pinnedProperty }) => (pinnedProperty ? '100%' : '120px')};
    ${({ pinnedProperty }) =>
      pinnedProperty &&
      css`
        max-height: 140px;
      `}
  }

  @media (${mobileBreakpoint}) {
    display: flex;
    order: -1;
    margin: 10px 0;
    padding: 0;
  }
`;

export const FooterThumbImages = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 10px 0 0;

  ${ThumbImage} {
    height: 160px;
  }

  @media (${mobileBreakpoint}) {
    display: none;
  }
`;

export const TierBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 3;
  background-color: rgba(255, 255, 255, 0.3);
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: bold;
  font-size: 14px;
  color: ${({ theme }) => theme.textDark};
`;

export const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-flow: row;
`;

export const SummaryItem = styled.div<{ $padding?: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 20px;

  ${({ $padding }) =>
    $padding &&
    css`
      padding: 4px 10px;
      border-radius: 40px;
    `}

  & > svg {
    color: ${({ theme, $padding }) => ($padding ? theme.textMid : theme.textLight)};
    margin: 0 ${({ $padding }) => ($padding ? 5 : 10)}px 0 0;
  }
`;

export const Tagline = styled(Text)`
  padding: 8px;
  border-radius: 4px;
  width: 100%;
`;
